import React from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface CampaignStats {
  totalContacts: number;
  processedContacts: number;
  successContacts: number;
  failedContacts: number;
  successRate: string;
  skippedContacts?: number;
  cancelledContacts?: number;
  emailReadCount?: number;
}

interface ExecutionStatisticsProps {
  campaignStats: CampaignStats | null;
  datasetCount: number;
  datasetLoading: boolean;
  statsLoading: boolean;
  statsError: string | null;
  onRefresh?: () => void;
}

export const ExecutionStatistics: React.FC<ExecutionStatisticsProps> = ({ campaignStats, datasetCount, datasetLoading, statsLoading, statsError }) => {
  const theme = useTheme();
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.heading?.main ?? theme.palette.secondary.main }}>
            Execution Statistics
          </Typography>
          {statsLoading && <CircularProgress size={20} />}
        </Box>

        {statsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {statsError}
          </Alert>
        )}

        {campaignStats ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                  <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                    <Box display="flex" alignItems="center">
                      <TrendingUpIcon sx={{ mr: 1, fontSize: 20 }} />
                      Statistics
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Contacts</TableCell>
                  <TableCell>
                    {datasetLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      datasetCount
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Filtered Contacts</TableCell>
                  <TableCell>{campaignStats.totalContacts}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Processed Contacts</TableCell>
                  <TableCell>{campaignStats.processedContacts}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Successful Contacts</TableCell>
                  <TableCell sx={{ color: theme.palette.success.main }}>{campaignStats.successContacts}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Failed Contacts</TableCell>
                  <TableCell sx={{ color: theme.palette.error.main }}>{campaignStats.failedContacts}</TableCell>
                </TableRow>
                {campaignStats.totalContacts > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Success Rate</TableCell>
                    <TableCell>
                      <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
                        {campaignStats.successRate}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {statsLoading ? 'Loading statistics...' : 'No statistics available yet.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ExecutionStatistics;


